import { ChangeDetectionStrategy, Component, ElementRef, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Location as Location_2 } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { trigger, transition, animate, keyframes, style } from '@angular/animations';

import { Store } from '@ngrx/store';
import {
  BehaviorSubject,
  catchError,
  combineLatest,
  distinctUntilChanged,
  filter,
  forkJoin,
  iif,
  map,
  Observable,
  of,
  startWith,
  Subject,
  Subscription,
  switchMap,
  take,
  tap,
  throwError,
  withLatestFrom,
} from 'rxjs';

import {
  GlobalBookingActions,
  globalBookingDates,
  globalBookingDiscountCode,
  globalBookingPaxAmount,
  globalBookingTravelClass,
  globalBookingTripType,
  globalBookingLocations,
  LocationPair,
} from '@fcom/common/store';
import { GlobalBookingTravelClass, LatLng } from '@fcom/core';
import { TripType, FINNISH_SITES } from '@fcom/core/constants';
import { finShare, retryWithBackoff } from '@fcom/rx';
import { ButtonTheme, ButtonSize, ButtonMode, TabTheme, LoaderTheme } from '@fcom/ui-components';
import { GlobalBookingTripDates, RecommendationService, HotjarService, HotjarCaptureEvent } from '@fcom/common';
import { Amount, PaxAmount } from '@fcom/dapi';
import { LocationRouteCffService, StorageService } from '@fcom/core/services';
import { LanguageService } from '@fcom/ui-translate';
import { Location, LocationGeoLocData, LocationRouteCffData, LocationSuggestions } from '@fcom/core-api';
import { akamaiGeolocation, loginStatus, profile } from '@fcom/core/selectors';
import { isNotBlank, isPresent, LocalDate, unsubscribe } from '@fcom/core/utils';
import { LoginStatus, Profile } from '@fcom/core-api/login';
import { BookingAppState } from '@fcom/common/interfaces/booking';
import { AirCalendarList } from '@fcom/dapi/api/models';
import { mapCffsToTravelClasses } from '@fcom/dapi/utils';
import { equals } from '@fcom/core/utils/equals';
import { LocationsService } from '@fcom/locations/services/locations.service';

import { Locations } from '../../interfaces/location-pair.interface';
import {
  createDeeplinkPathFromFlightSearchParams,
  createPreviousSearches,
  getStartingFromPrice,
} from '../../utils/utils';
import {
  AmountUpdateType,
  DatePickerPrices,
  FlightSearchParams,
  InstantSearchBaseParams,
  PaxUpdateEvent,
  WidgetTab,
} from '../../interfaces';
import { BookingWidgetCalendarService } from '../../services/booking-widget-calendar.service';
import { ANIMATION_DURATION, AVAILABLE_TRIP_TYPES, PREVIOUS_SEARCHES_KEY } from '../../constants';
import {
  activeTab,
  airCalendarPrices,
  followingMonthPrices,
  fullYearPrices,
  histogramPrices,
} from '../../store/selectors/booking-widget.selector';
import { BookingWidgetActions } from '../../store';
import { PreviousSearch } from '../../interfaces/previous-searches.interface';
import { BookingWidgetGtmService } from '../../services/booking-widget-gtm.service';
import { getGtmPassengers } from '../../utils/utils.gtm';

const widgetTabMap = {
  0: WidgetTab.FLIGHT,
  1: WidgetTab.AWARD,
  2: WidgetTab.AM,
};

interface PriceParams {
  tripType: TripType;
  travelClass: GlobalBookingTravelClass;
  locations: Locations;
  travelDates?: GlobalBookingTripDates;
  paxAmount: PaxAmount;
}

@Component({
  selector: 'fin-booking-widget-v2',
  templateUrl: './booking-widget.component.html',
  styleUrls: ['./booking-widget.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [
    trigger('smoothGrow', [
      transition('* => *', [
        animate(
          `${ANIMATION_DURATION}ms cubic-bezier(0.4, 0.0, 0.2, 1)`,
          keyframes([style({ height: '{{ startHeight }}' }), style({ height: '*' })])
        ),
      ]),
    ]),
  ],
})
export class BookingWidgetComponent implements OnInit, OnDestroy {
  readonly ButtonTheme = ButtonTheme;
  readonly ButtonSize = ButtonSize;
  readonly ButtonMode = ButtonMode;
  readonly TripType = TripType;
  readonly TabTheme = TabTheme;
  readonly WidgetTab = WidgetTab;
  readonly LoginStatus = LoginStatus;
  readonly LoaderTheme = LoaderTheme;

  @Input()
  compactMode = false;

  @Input()
  headingLevel: 1 | 2 = 1;

  @Input()
  defaultLocations$: Observable<LocationPair[]> = of([]);

  @Input()
  enableAurinkomatkat = true;

  tripType$: Observable<TripType>;
  travelClass$: Observable<GlobalBookingTravelClass>;
  availableTravelClasses$: Observable<GlobalBookingTravelClass[]>;
  availableTripTypes$: Observable<TripType[]>;
  paxAmount$: Observable<PaxAmount>;
  suggestedLocations$: Observable<LocationSuggestions>;
  trendingDestinations$: Observable<Location[]>;
  locations$: Observable<LocationPair>;
  multiCityLocations$: Observable<LocationPair[]>;
  travelDates$: Observable<GlobalBookingTripDates>;
  flexibleDates$ = new BehaviorSubject<boolean>(false);
  startingPrice$: Observable<Amount> = of(null);
  flightSearchParams$: Observable<FlightSearchParams>;
  continueEnabled$: Observable<boolean>;
  prices$: Observable<DatePickerPrices>;
  airCalendarPrices$: Observable<AirCalendarList>;
  priceRequestBaseParams$: Observable<InstantSearchBaseParams>;
  discountCode$: Observable<string>;
  activeTab$: Observable<WidgetTab>;
  profile$: Observable<Profile>;
  loginStatus$: Observable<LoginStatus>;
  loading$: Subject<boolean> = new Subject<boolean>();
  setPaxDetailsGtm$: Subject<boolean> = new Subject<boolean>();
  rootContainerStartHeight$: Observable<number> = of(0);
  showCompact$ = new BehaviorSubject<boolean>(this.compactMode);
  isInitialRender: boolean;
  amTabEnabled$: Observable<boolean>;
  enableAmWidget: boolean;
  subscription = new Subscription();
  initAm$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  isAm$: Observable<boolean>;

  @ViewChild('widgetRoot', { static: true })
  widgetRoot: ElementRef;

  constructor(
    private store$: Store<BookingAppState>,
    private locationRouteCffService: LocationRouteCffService,
    private languageService: LanguageService,
    private recommendationService: RecommendationService,
    private bookingWidgetCalendarService: BookingWidgetCalendarService,
    private router: Router,
    private route: ActivatedRoute,
    private storageService: StorageService,
    private bookingWidgetGtmService: BookingWidgetGtmService,
    private location: Location_2,
    private hotjarService: HotjarService,
    private locationService: LocationsService
  ) {}

  ngOnInit(): void {
    const activeTabFromRouteParams = this.route.snapshot?.queryParams['tab'] ?? this.route.snapshot?.data.tab;
    if (activeTabFromRouteParams && Object.values(WidgetTab).includes(activeTabFromRouteParams)) {
      this.store$.dispatch(BookingWidgetActions.setActiveTab({ activeTab: activeTabFromRouteParams }));

      this.router.navigate([], {
        queryParams: { tab: null },
        queryParamsHandling: 'merge',
      });
    }

    // This is to check whether there is any location history saved (if it is the initial render or not)
    // This is coupled with the compact widget behavior since we only want to compact mode to happen on first render of the app.
    this.isInitialRender = (this.location.getState() as { navigationId: number })?.navigationId === 1;

    if (this.compactMode && this.isInitialRender) {
      this.store$.dispatch(GlobalBookingActions.resetSelection());

      this.showCompact$.next(true);
    }

    this.amTabEnabled$ = of(this.enableAurinkomatkat && FINNISH_SITES.includes(this.languageService.langValue));

    // Selections

    this.paxAmount$ = this.store$.pipe(globalBookingPaxAmount(), finShare());
    this.tripType$ = this.store$.pipe(
      globalBookingTripType(),
      map((tripType: TripType) => tripType ?? TripType.RETURN),
      finShare()
    );
    this.travelDates$ = this.store$.pipe(globalBookingDates(), finShare());
    this.travelClass$ = this.store$.pipe(globalBookingTravelClass()).pipe(
      map((travelClass: GlobalBookingTravelClass) =>
        travelClass === GlobalBookingTravelClass.FIRST ? GlobalBookingTravelClass.BUSINESS : travelClass
      ),
      distinctUntilChanged(),
      finShare()
    );

    const bookingLocations$ = this.store$.pipe(
      globalBookingLocations(),
      distinctUntilChanged((prev, next) => prev.length === next.length && next.every((l, i) => equals(l, prev[i]))),
      finShare()
    );
    this.locations$ = bookingLocations$.pipe(
      map((locations) => locations[0]),
      distinctUntilChanged((prev, next) => equals(prev, next)),
      finShare()
    );
    this.multiCityLocations$ = bookingLocations$;

    this.discountCode$ = this.store$.pipe(
      globalBookingDiscountCode(),
      map((discountCode: string) => (isPresent(discountCode) ? discountCode : null)),
      finShare()
    );

    // Utility

    this.profile$ = this.store$.pipe(profile(), finShare());

    this.activeTab$ = this.store$.pipe(
      activeTab(),
      tap((tab) => {
        if (tab === WidgetTab.AM) {
          this.initAm$.next(true);
        }
      }),
      map((tab: WidgetTab) => tab ?? WidgetTab.FLIGHT),
      distinctUntilChanged(),
      finShare()
    );

    this.isAm$ = this.activeTab$.pipe(map((tab) => tab === WidgetTab.AM));

    this.availableTravelClasses$ = combineLatest([
      bookingLocations$.pipe(
        map((locations) => this.getValidlocationPairs(locations)),
        distinctUntilChanged(
          (prev, next) =>
            prev.length === next.length &&
            next.every(
              (l, i) =>
                l.origin?.locationCode === prev[i]?.origin?.locationCode &&
                l.destination?.locationCode === prev[i]?.destination?.locationCode
            )
        )
      ),
      this.activeTab$,
      this.profile$.pipe(startWith(undefined)),
    ]).pipe(
      filter(
        ([locations, activeTab, profile]) =>
          locations.length > 0 &&
          (activeTab === WidgetTab.FLIGHT || (activeTab === WidgetTab.AWARD && isPresent(profile)))
      ),
      switchMap(([validLocations, tab]) =>
        forkJoin(
          validLocations.map(({ origin, destination }) =>
            this.locationRouteCffService.routeCffsFor(
              origin.locationCode,
              destination.locationCode,
              tab === WidgetTab.AWARD ? 'true' : 'false',
              this.languageService.localeValue
            )
          )
        )
      ),
      map((routeCffDataForFlights: LocationRouteCffData[]) => this.mapCffsToTravelClasses(routeCffDataForFlights)),
      finShare()
    );

    this.availableTripTypes$ = this.activeTab$.pipe(
      map((tab) =>
        tab === WidgetTab.AWARD ? AVAILABLE_TRIP_TYPES.filter((t) => t !== TripType.MULTICITY) : AVAILABLE_TRIP_TYPES
      )
    );

    this.flightSearchParams$ = combineLatest([
      this.tripType$,
      this.travelClass$,
      this.locations$,
      this.multiCityLocations$,
      this.paxAmount$,
      this.travelDates$,
      this.activeTab$,
      this.discountCode$,
    ]).pipe(
      map(([tripType, travelClass, locations, multiCityLocations, paxAmount, travelDates, tab, discountCode]) => ({
        tripType,
        travelClass,
        locations: this.mapLocationsToSearchParams(locations, multiCityLocations, tripType),
        paxAmount,
        travelDates,
        locale: this.languageService.localeValue,
        isAward: tab === WidgetTab.AWARD,
        promoCode: discountCode,
      })),
      finShare()
    );

    this.suggestedLocations$ = this.fetchSuggestedLocations();
    this.trendingDestinations$ = this.fetchTrendingLocations();
    this.profile$ = this.store$.pipe(profile(), startWith(undefined), finShare());
    this.loginStatus$ = this.store$.pipe(loginStatus());

    this.continueEnabled$ = this.flightSearchParams$.pipe(
      map((flightSearchParams) => this.validateFlightSearchParams(flightSearchParams))
    );

    // Prices

    this.priceRequestBaseParams$ = combineLatest([
      this.tripType$,
      this.travelClass$,
      this.locations$.pipe(
        distinctUntilChanged(
          (previous, current) =>
            previous?.origin?.locationCode === current?.origin?.locationCode &&
            previous?.destination?.locationCode === current?.destination?.locationCode
        )
      ),
      this.activeTab$,
    ]).pipe(
      tap(() => this.store$.dispatch(BookingWidgetActions.clearServiceNotificationWarnings())),
      filter(([, , , tab]) => tab === WidgetTab.FLIGHT),
      withLatestFrom(this.paxAmount$),
      map(([[tripType, travelClass, locations], paxAmount]) => ({
        tripType,
        travelClass,
        locations,
        paxAmount,
      })),
      filter((params) => this.priceParamsAreValid(params) && params.tripType !== TripType.MULTICITY),
      map((params) => this.mapToPriceRequestBaseParams(params)),
      distinctUntilChanged((prev, next) => !this.priceBaseParamsChanged(prev, next)),
      finShare()
    );

    this.airCalendarPrices$ = this.store$.pipe(airCalendarPrices(), finShare());
    this.prices$ = combineLatest([
      this.store$.pipe(fullYearPrices()),
      this.store$.pipe(followingMonthPrices()),
      this.store$.pipe(histogramPrices()),
      this.tripType$,
      this.travelDates$,
      this.activeTab$,
    ]).pipe(
      map(([fullYear, followingMonth, histogram, tripType, { departureDate, returnDate }, tab]) => {
        if (tab !== WidgetTab.FLIGHT) {
          return undefined;
        }

        const onlyDepartureSelectedAndNotOneway =
          isPresent(departureDate) && tripType !== TripType.ONEWAY && !isPresent(returnDate);
        const prices = onlyDepartureSelectedAndNotOneway ? followingMonth : fullYear;

        const pricesWithSelections =
          departureDate && returnDate ? { ...prices, [returnDate?.id]: followingMonth?.[returnDate?.id] } : prices;

        return {
          calendar: pricesWithSelections,
          histogram,
        };
      }),
      finShare()
    );

    this.startingPrice$ = combineLatest([this.prices$, this.travelDates$, this.tripType$]).pipe(
      map(([prices, travelDates, tripType]) => getStartingFromPrice(prices, travelDates, tripType === TripType.ONEWAY))
    );

    // For smooth grow animation we calculate the start height for the transition when the animation is triggered
    this.rootContainerStartHeight$ = this.showCompact$.pipe(map(() => this.widgetRoot.nativeElement.scrollHeight));

    // Subscriptions

    this.fetchUserLocationAndSetDefaultOrigin();

    this.subscription.add(
      combineLatest([this.locationService.retrieveAllLocations(), bookingLocations$])
        .pipe(
          take(1),
          map(([locations, bookingLocations]) =>
            bookingLocations.map(({ origin, destination }) => ({
              origin: locations[origin?.locationCode],
              destination: locations[destination?.locationCode],
            }))
          )
        )
        .subscribe((locations) => {
          this.store$.dispatch(GlobalBookingActions.setLocations({ locations }));
        })
    );

    this.subscription.add(
      this.locations$
        .pipe(
          map(({ origin, destination }) => isNotBlank(origin?.locationCode) && isNotBlank(destination?.locationCode)),
          distinctUntilChanged()
        )
        .subscribe((hasBothLocations) => {
          if (!hasBothLocations) {
            BookingWidgetActions.resetPrices();
          }

          if (this.compactMode) {
            this.showCompact$.next(!hasBothLocations);
          }
        })
    );

    // We don't offer multi-city for award flow so we default to return if multi-city is selected
    this.subscription.add(
      this.activeTab$
        .pipe(
          withLatestFrom(this.tripType$),
          filter(([tab, tripType]) => tab === WidgetTab.AWARD && tripType === TripType.MULTICITY)
        )
        .subscribe(() => {
          this.setTripType(TripType.RETURN);
        })
    );

    this.subscription.add(
      this.setPaxDetailsGtm$
        .pipe(
          filter((value) => Boolean(value)),
          withLatestFrom(this.paxAmount$),
          map(([_, paxAmount]) => paxAmount)
        )
        .subscribe((paxDetails: PaxAmount) => {
          const { adults, children, infants } = getGtmPassengers(paxDetails);
          this.bookingWidgetGtmService.trackElementEvent(
            'pax-amount',
            `MAINPAX: ${adults}, CHD: ${children}, INF: ${infants}, MAINPAXTYPE: ADT`
          );
          this.setPaxDetailsGtm$.next(false);
        })
    );

    // We fetch calendar prices whenever user changes the selections
    this.subscription.add(
      this.priceRequestBaseParams$
        .pipe(
          switchMap((baseParams) =>
            this.bookingWidgetCalendarService.getPricesForFullYear({
              ...baseParams,
              startDate: LocalDate.now().id,
            })
          )
        )
        .subscribe(({ fullYear, histogram }) => {
          this.store$.dispatch(BookingWidgetActions.setFullYearPrices({ prices: fullYear }));
          this.store$.dispatch(BookingWidgetActions.setHistogramPrices({ prices: histogram }));
        })
    );

    // Full month prices need to be fetched only for round trips
    this.subscription.add(
      combineLatest([
        this.priceRequestBaseParams$,
        this.travelDates$.pipe(distinctUntilChanged((prev, next) => prev.departureDate === next.departureDate)),
      ])
        .pipe(
          filter(([{ oneway }, travelDates]) => travelDates?.departureDate && !oneway),
          switchMap(([baseParams, travelDates]) =>
            this.bookingWidgetCalendarService.getPricesForFollowingMonth({
              ...baseParams,
              departureDate: travelDates.departureDate?.id,
              numberOfDays: 30,
            })
          )
        )
        .subscribe((prices) => {
          this.store$.dispatch(BookingWidgetActions.setFollowingMonthPrices({ prices }));
        })
    );

    this.subscription.add(
      this.defaultLocations$
        .pipe(
          take(1),
          withLatestFrom(this.locations$),
          map(([locationsToBeSet, locationFromStore]) => {
            const origin = isPresent(locationsToBeSet?.[0]?.origin?.locationCode)
              ? locationsToBeSet?.[0]?.origin
              : locationFromStore?.origin;
            const destination = isPresent(locationsToBeSet?.[0]?.destination?.locationCode)
              ? locationsToBeSet?.[0]?.destination
              : locationFromStore?.destination;

            return { origin, destination };
          }),
          finShare()
        )
        .subscribe(({ origin, destination }) => {
          this.store$.dispatch(
            GlobalBookingActions.updateLocationPair({
              locationPair: { origin, destination },
              index: 0,
            })
          );
        })
    );

    this.subscription.add(
      this.availableTravelClasses$.subscribe((availableTravelClasses: GlobalBookingTravelClass[]) => {
        this.store$.dispatch(
          GlobalBookingActions.setTravelClass({
            travelClass: availableTravelClasses.includes(GlobalBookingTravelClass.MIXED)
              ? GlobalBookingTravelClass.MIXED
              : availableTravelClasses[0],
          })
        );
      })
    );
  }

  ngOnDestroy(): void {
    unsubscribe(this.subscription);
  }

  setTripType(tripType: TripType): void {
    this.bookingWidgetGtmService.trackElementEvent('travel-type', tripType.toUpperCase());
    this.store$.dispatch(GlobalBookingActions.setTripType({ tripType }));
  }

  setTravelClass(travelClass: GlobalBookingTravelClass): void {
    this.bookingWidgetGtmService.trackElementEvent('travel-class', travelClass);
    this.store$.dispatch(GlobalBookingActions.setTravelClass({ travelClass }));
  }

  setPaxAmount({ paxType, amount, updateType }: PaxUpdateEvent): void {
    if (updateType === AmountUpdateType.DECREMENT) {
      this.store$.dispatch(GlobalBookingActions.decreasePaxAmountField({ field: paxType, decrement: amount }));
    } else {
      this.store$.dispatch(GlobalBookingActions.increasePaxAmountField({ field: paxType, increment: amount }));
    }
    this.setPaxDetailsGtm$.next(true);
  }

  updateLocations({ origin, destination }: LocationPair): void {
    this.bookingWidgetGtmService.trackElementEvent(
      'locations',
      `${origin?.locationCode} - ${destination?.locationCode}`
    );

    this.store$.dispatch(GlobalBookingActions.setLocations({ locations: [{ origin, destination }] }));
  }

  updateLocationPair({ origin, destination }: LocationPair, index = 0): void {
    this.bookingWidgetGtmService.trackElementEvent(
      'locations',
      `${origin?.locationCode} - ${destination?.locationCode}`
    );

    this.store$.dispatch(GlobalBookingActions.updateLocationPair({ locationPair: { origin, destination }, index }));
  }

  setTravelDates(dates: GlobalBookingTripDates, isAirCalendar = false): void {
    if (!isAirCalendar) {
      this.bookingWidgetGtmService.trackElementEvent(
        'travel-dates',
        `DEPARTURE: ${dates.departureDate}, RETURN: ${dates.returnDate}`
      );
    }
    this.store$.dispatch(GlobalBookingActions.setDates({ dates }));
  }

  setActiveTab({ index }: { index: number; data: unknown }): void {
    this.store$.dispatch(BookingWidgetActions.setActiveTab({ activeTab: widgetTabMap[index] }));
  }

  navigateToBookingFlow(fromMatrix = false, airCalendarPrice: string | undefined = undefined): void {
    this.subscription.add(
      this.continueEnabled$
        .pipe(
          take(1),
          filter(Boolean),
          withLatestFrom(this.flightSearchParams$, this.flexibleDates$, this.startingPrice$),
          switchMap(([_, flightSearchParams, flexibleDates, startingPrice]) => {
            this.loading$.next(true);
            this.sendRecommendationsFlightSearchEvent(flightSearchParams);

            if (fromMatrix) {
              return of({ continueToBooking: true, flightSearchParams, airCalendarPrice, startingPrice });
            } else {
              return iif(
                () => flexibleDates || flightSearchParams.isAward,
                this.bookingWidgetCalendarService.getAirCalendar(flightSearchParams).pipe(map(() => false)),
                this.bookingWidgetCalendarService.checkFlightAvailabilityAndContinue(flightSearchParams)
              ).pipe(
                map((continueToBooking) => ({
                  continueToBooking,
                  flightSearchParams,
                  startingPrice,
                  airCalendarPrice,
                }))
              );
            }
          }),
          tap(() => this.loading$.next(false)),
          filter(({ continueToBooking }) => continueToBooking),
          map(({ flightSearchParams, startingPrice, airCalendarPrice }) => {
            this.setPreviousSearch(flightSearchParams);
            return {
              deeplink: createDeeplinkPathFromFlightSearchParams({
                ...flightSearchParams,
                locale: this.languageService.langValue,
              }),
              instantSearchMonitoring: {
                airCalendarPrice,
                instantSearchPrice: startingPrice?.amount,
              },
            };
          })
        )
        .subscribe(({ deeplink, instantSearchMonitoring }) => {
          this.store$.dispatch(BookingWidgetActions.setInstantSearchMonitoring({ instantSearchMonitoring }));
          this.router.navigateByUrl(deeplink);
        })
    );
  }

  sendRecommendationsFlightSearchEvent(params: FlightSearchParams): void {
    const departureLocation = params.locations[0].origin;
    const destinationLocation = params.locations[0].destination;

    this.subscription.add(
      this.recommendationService
        .sendFlightSearchEvent(
          params.travelDates.departureDate.toISOString().substring(0, 10),
          params.travelDates.returnDate?.toISOString().substring(0, 10),
          departureLocation.type === 'airport' ? departureLocation.locationCode : undefined,
          destinationLocation.type === 'airport' ? destinationLocation.locationCode : undefined,
          departureLocation.type === 'city' ? departureLocation.locationCode : undefined,
          destinationLocation.type === 'city' ? destinationLocation.locationCode : undefined,
          // combining adults + c15s was in the initial requirement and logic was the same in the old widget
          params.paxAmount.adults + params.paxAmount.c15s,
          params.paxAmount.children + params.paxAmount.infants
        )
        .subscribe()
    );
  }

  trackAmLinkClick(): void {
    this.bookingWidgetGtmService.trackElementEvent('aurinkomatkat-search');
  }

  startHotjarCapture(): void {
    this.subscription.add(this.hotjarService.startCapture(HotjarCaptureEvent.BOOKING_WIDGET).subscribe());
  }

  onAmLocationChanged(): void {
    if (this.compactMode && this.showCompact$.getValue()) {
      this.showCompact$.next(false);
    }
  }

  private fetchUserLocationAndSetDefaultOrigin(): void {
    this.subscription.add(
      combineLatest([this.locations$, this.defaultLocations$, this.store$.pipe(akamaiGeolocation())])
        .pipe(
          take(1),
          filter(
            ([{ origin }, defaultLocations]) =>
              !isPresent(origin) || (defaultLocations.length > 0 && !isPresent(defaultLocations[0]?.origin))
          ),
          map(([_, __, geoLocation]) => geoLocation)
        )
        .pipe(
          switchMap((value: LatLng) =>
            this.locationRouteCffService.geolocMatchFor(value.lat, value.lng, this.languageService.localeValue)
          ),
          switchMap((location: LocationGeoLocData) =>
            this.locationRouteCffService.bestGuessFor(location.item.locationCode, this.languageService.localeValue)
          ),
          retryWithBackoff(2),
          catchError((err: unknown) => {
            return throwError(() => err);
          }),
          withLatestFrom(this.defaultLocations$),
          switchMap(([cffLocation, defaultLocations]) =>
            iif(
              () => isPresent(defaultLocations?.[0]?.destination?.locationCode),
              this.locationRouteCffService
                .bestGuessFor(defaultLocations?.[0]?.destination?.locationCode, this.languageService.localeValue)
                .pipe(map((destination) => [cffLocation, destination])),
              of([cffLocation])
            )
          ),
          finShare()
        )
        .subscribe(([cffLocation, destination]) => {
          this.updateLocationPair({
            origin: cffLocation,
            ...(isPresent(destination) && { destination }),
          });
        })
    );
  }

  private fetchSuggestedLocations(): Observable<LocationSuggestions> {
    return this.languageService.countryCode.pipe(
      switchMap((countryCode) =>
        this.locationRouteCffService.getSuggestedLocations(countryCode, this.languageService.localeValue)
      ),
      finShare()
    );
  }

  private fetchTrendingLocations(): Observable<Location[]> {
    return this.locations$.pipe(
      switchMap((locations) => {
        const originLocationCode = locations?.origin?.locationCode;
        if (originLocationCode) {
          return this.recommendationService
            .getTrendingDestinations(originLocationCode, 0, 10)
            .pipe(catchError(() => of([])));
        } else {
          return of([]);
        }
      }),
      finShare()
    );
  }

  private validateFlightSearchParams({
    tripType,
    travelClass,
    locations,
    paxAmount,
    travelDates,
  }: FlightSearchParams): boolean {
    const travelClassIsValid = isPresent(travelClass);
    const paxAmountIsValid = paxAmount.adults >= 1;
    const locationsAreValid = locations.every(
      (l) => isPresent(l?.origin?.locationCode) && isPresent(l?.destination?.locationCode)
    );
    const departureIsValid = isPresent(travelDates?.departureDate);
    const returnIsValid = isPresent(travelDates?.returnDate);
    const travelDatesAreValid = tripType === TripType.ONEWAY ? departureIsValid : departureIsValid && returnIsValid;

    return travelClassIsValid && paxAmountIsValid && locationsAreValid && travelDatesAreValid;
  }

  private priceParamsAreValid({ locations, travelClass, tripType, paxAmount }: PriceParams): boolean {
    return (
      isPresent(travelClass) &&
      isPresent(tripType) &&
      isPresent(locations?.origin?.locationCode) &&
      isPresent(locations?.destination?.locationCode) &&
      paxAmount?.adults >= 1
    );
  }

  private priceBaseParamsChanged(prev: InstantSearchBaseParams, next: InstantSearchBaseParams): boolean {
    return (
      prev.departureLocationCode !== next.departureLocationCode ||
      prev.destinationLocationCode !== next.destinationLocationCode ||
      prev.travelClass !== next.travelClass ||
      prev.oneway !== next.oneway
    );
  }

  private mapToPriceRequestBaseParams({
    tripType,
    travelClass,
    locations,
    paxAmount,
  }: PriceParams): InstantSearchBaseParams {
    return {
      departureLocationCode: locations.origin.locationCode,
      destinationLocationCode: locations.destination.locationCode,
      travelClass: travelClass === GlobalBookingTravelClass.MIXED ? undefined : travelClass,
      adults: paxAmount.adults + paxAmount.c15s,
      children: paxAmount.children,
      infants: paxAmount.infants,
      numberOfDays: 360,
      oneway: tripType === TripType.ONEWAY,
      directFlights: false,
      locale: this.languageService.localeValue,
    };
  }

  // TODO: could probably be generalized
  private mapLocationsToSearchParams = (
    locations: LocationPair,
    multiCityLocations: LocationPair[],
    tripType: TripType
  ): LocationPair[] => {
    return tripType === TripType.MULTICITY
      ? multiCityLocations
      : tripType === TripType.RETURN
        ? [
            locations,
            {
              origin: locations.destination,
              destination: locations.origin,
            },
          ]
        : [locations];
  };

  private setPreviousSearch = (flightSearchParams: FlightSearchParams): void => {
    if (flightSearchParams.isAward || flightSearchParams.tripType === TripType.MULTICITY) {
      return;
    }

    let previousSearches: PreviousSearch[];
    const previousSearchesStorage = this.storageService.LOCAL.get(PREVIOUS_SEARCHES_KEY);

    try {
      previousSearches = JSON.parse(previousSearchesStorage) ?? [];
    } catch {
      previousSearches = [];
    }

    const updatedPreviousSearches = createPreviousSearches(flightSearchParams, previousSearches);
    this.storageService.LOCAL.set(PREVIOUS_SEARCHES_KEY, JSON.stringify(updatedPreviousSearches));
  };

  private getValidlocationPairs(locationPairs: LocationPair[]): LocationPair[] {
    return locationPairs.filter(
      ({ origin, destination }) => isNotBlank(origin?.locationCode) && isNotBlank(destination?.locationCode)
    );
  }

  private getUniqueTravelClasses(travelClasses: GlobalBookingTravelClass[]): GlobalBookingTravelClass[] {
    return [...new Set(travelClasses)];
  }

  private mapCffsToTravelClasses(locationRouteCffData: LocationRouteCffData[]): GlobalBookingTravelClass[] {
    const travelClassesForFlights = locationRouteCffData
      .map((cffsForFlight) =>
        mapCffsToTravelClasses(cffsForFlight).filter((travelClass) => travelClass !== GlobalBookingTravelClass.FIRST)
      )
      .flat()
      .sort((a, b) => {
        const keys = Object.keys(GlobalBookingTravelClass);
        return keys.indexOf(a) - keys.indexOf(b);
      });

    return this.getUniqueTravelClasses(travelClassesForFlights);
  }
}
